import type { ParseKeys } from 'i18next';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutline } from '@/components/Icons/CheckCircleOutline';
import { DoubleCheckCircle } from '@/components/Icons/DoubleCheckCircle';
import { PencilCircle } from '@/components/Icons/PencilCircle';
import { Person } from '@/components/Icons/Person';
import { Star } from '@/components/Icons/Star';
import { StarCircle } from '@/components/Icons/StarCircle';
import { StarFilled } from '@/components/Icons/StarFilled';
import { SurveyQuestionType } from '@/types/components';
import type { SurveyAnswer, SurveyAnsweredQuestion } from '@/types/survey';

const RATING_STARS = 5;
const STAR_STROKE_WIDTH = '1';
const ROW_COLUMNS =
  'grid grid-cols-[1fr_58px_44px] items-center px-4 md:grid-cols-[1fr_80px_44px]';

const questionTypeIcons: Record<SurveyQuestionType, ReactNode> = {
  [SurveyQuestionType.SingleAnswer]: (
    <CheckCircleOutline className="size-4 shrink-0" />
  ),
  [SurveyQuestionType.MultiAnswer]: (
    <DoubleCheckCircle className="size-4 shrink-0" />
  ),
  [SurveyQuestionType.Feedback]: <StarCircle className="size-4 shrink-0" />,
  [SurveyQuestionType.FreeForm]: <PencilCircle className="size-4 shrink-0" />,
};

const questionTypeLabels: Record<SurveyQuestionType, ParseKeys> = {
  [SurveyQuestionType.SingleAnswer]: 'mail.surveys.detail.type.singleAnswer',
  [SurveyQuestionType.MultiAnswer]: 'mail.surveys.detail.type.multiAnswer',
  [SurveyQuestionType.Feedback]: 'mail.surveys.detail.type.rating',
  [SurveyQuestionType.FreeForm]: 'mail.surveys.detail.type.freeForm',
};

interface AnswerRow {
  answer: SurveyAnswer;
  label: string;
}

const buildAnswerRows = (
  answers: SurveyAnswer[],
  otherAnswerLabel: string,
): AnswerRow[] => {
  let answerNumber = 0;

  return answers.map((answer) => {
    if (answer.isOtherAnswer) {
      return { answer, label: otherAnswerLabel };
    }

    answerNumber += 1;

    return { answer, label: `${answerNumber}.` };
  });
};

interface Props {
  index: number;
  question: SurveyAnsweredQuestion;
}

export const SurveyResultsQuestion: React.FC<Props> = ({ index, question }) => {
  const { t } = useTranslation();

  const { type, description, answers } = question;
  const isFeedback = type === SurveyQuestionType.Feedback;

  const countDigits = answers.map((item) => String(item.count).length);
  const maxCountDigits = Math.max(1, ...countDigits);

  const rows = buildAnswerRows(
    answers,
    t('mail.surveys.detail.otherAnswerShort'),
  );

  const ratingStars = (rating: number) => (
    <div className="flex items-center gap-1">
      {Array.from({ length: RATING_STARS }, (_, position) =>
        position < rating ? (
          <StarFilled key={position} className="size-4 text-accent" />
        ) : (
          <Star
            key={position}
            strokeWidth={STAR_STROKE_WIDTH}
            className="size-4 text-subtle-foreground"
          />
        ),
      )}
    </div>
  );

  return (
    <div className="flex flex-col rounded-xl bg-on-primary-surface">
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-caption-12 text-subtle-foreground">
            {t('mail.surveys.detail.questionNumber', { index })}
          </span>
          <span className="text-caption-12 flex shrink-0 items-center gap-1 text-subtle-foreground md:text-body-14 md:gap-2">
            {questionTypeIcons[type]}
            {t(questionTypeLabels[type])}
          </span>
        </div>
        <p className="text-callout-16 text-foreground">{description}</p>
      </div>

      <div className="h-px bg-divider-surface" />

      <div className="flex flex-col gap-1 pt-4 md:pb-2">
        <div
          className={`${ROW_COLUMNS} text-caption-13 text-subtle-foreground md:text-caption-12`}
        >
          <span>{t('mail.surveys.detail.answerOptions')}</span>
          <span>{t('mail.surveys.detail.percentage')}</span>
          <span>{t('mail.surveys.detail.count')}</span>
        </div>

        <div className="flex flex-col md:gap-1">
          {rows.map(({ answer, label }, position) => (
            <div
              key={position}
              className={`${ROW_COLUMNS} text-body-14 py-2 text-foreground ${answer.isSelectedByCurrentUser ? 'bg-info-surface' : ''}`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="w-[27px] shrink-0 border-r border-divider-surface pr-2">
                  {label}
                </span>
                {isFeedback && ratingStars(Number(answer.description))}
                {!isFeedback && (
                  <span className="truncate">{answer.description}</span>
                )}
              </div>
              <span>{answer.percentage}%</span>
              <span className="flex items-center gap-1">
                <span
                  style={{ minWidth: `${maxCountDigits}ch` }}
                  className="tabular-nums"
                >
                  {answer.count}
                </span>
                {answer.isSelectedByCurrentUser && (
                  <Person className="size-4 shrink-0 text-primary" />
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
